import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/auth-provider';
import toast from '../../utils/toast';
import { resolvePostAuthRoute } from '../../utils/post-auth-route';
import { normalizeUserRole } from '../../auth/roles';
import { PERMISSIONS } from '../../auth/permissions';
import { Shell, Card, HelperText } from '../onboarding/onboarding.styled';
import { PortalNoHome } from '../pages/portal/portal-no-home';
import { deskInPlan, DESK_NOT_IN_PLAN_REASON } from '../../utils/products';

// Reusable route guards. Because auth is resolved asynchronously (checkAuth runs
// after mount), we treat "no user yet but a token cookie exists" as LOADING and
// render a placeholder instead of flashing a redirect to login.
const Loading: React.FC = () => (
  <Shell>
    <Card>
      <HelperText>Loading…</HelperText>
    </Card>
  </Shell>
);

export const RequireAuth: React.FC<{ children: React.ReactElement }> = ({
  children,
}) => {
  const { user, authStatus } = useAuth();

  if (!user) {
    // httpOnly cookies are invisible to JS, so "is there a session?" is answered
    // by the /auth probe, not by peeking at a cookie: wait while it's loading.
    return authStatus === 'loading' ? <Loading /> : <Navigate to="/" replace />;
  }

  return children;
};


/**
 * A REFUSED ROUTE SAYS SO.
 *
 * Both gates below fail by rendering <Navigate replace/> and nothing else. A
 * concierge opening a bookmarked /dashboard, or a resident following a staff
 * deep link from an email, was teleported to their own home screen in silence —
 * no toast, no banner, no trace in the URL. From the inside that is
 * indistinguishable from the link being broken, or from the app losing the
 * click, and the reliable next move is to press it again.
 *
 * Announced from an effect rather than during render, because firing a toast
 * inside a render is a side effect in the wrong phase and React will run it
 * twice in StrictMode. `replace` is kept: the refused URL must not sit in
 * history where Back would walk straight into it again.
 */
export const RedirectWithReason: React.FC<{ to: string; reason: string }> = ({
  to,
  reason,
}) => {
  useEffect(() => {
    toast(reason);
  }, [reason]);

  return <Navigate to={to} replace />;
};

export const RequireRole: React.FC<{
  roles: string[];
  children: React.ReactElement;
}> = ({ roles, children }) => {
  const { user, onboarding, permissions, authStatus } = useAuth();

  if (!user) {
    return authStatus === 'loading' ? <Loading /> : <Navigate to="/" replace />;
  }
  // Onboarding-incomplete users are kept in the wizard on EVERY guarded route
  // (re-checked per navigation), not just once at bootstrap (#7). /onboarding
  // itself uses RequireAuth (not RequireRole), so there's no redirect loop.
  if (onboarding && !onboarding.completed) {
    return <Navigate to="/onboarding" replace />;
  }
  // `roles` holds canonical STAFF values; user.role may still be a legacy
  // spelling (a pre-migration DB row). Normalize both sides or every staff user
  // fails this gate and bounces between guarded routes.
  const role = normalizeUserRole(user.role);

  if (!role || !roles.map(normalizeUserRole).includes(role)) {
    // Wrong role → send them to where they belong, never a dead end, and TELL
    // them why they moved.
    return (
      <RedirectWithReason
        to={resolvePostAuthRoute(user, onboarding, permissions)}
        reason="That page is not available for your role. Here is your home screen."
      />
    );
  }

  return children;
};

// The front-desk screens need Lobbero Desk as a PRODUCT (Independent Stays IS1).
//
// The role gate cannot answer it: a Stays-only organisation's owner is still
// an owner. The API refuses every desk request with DESK_NOT_IN_PLAN anyway;
// this sends the person to their own home and says why, instead of opening a
// desk shell whose every tile fails. Absent `products` = Desk (as before).
export const RequireDesk: React.FC<{ children: React.ReactElement }> = ({
  children,
}) => {
  const { user, onboarding, permissions, authStatus } = useAuth();

  if (!user) {
    return authStatus === 'loading' ? <Loading /> : <Navigate to="/" replace />;
  }
  if (!deskInPlan(user)) {
    return (
      <RedirectWithReason
        to={resolvePostAuthRoute(user, onboarding, permissions)}
        reason={DESK_NOT_IN_PLAN_REASON}
      />
    );
  }

  return children;
};

// The same guard, keyed on a capability instead of a job title.
//
// The resident portal cannot be role-gated any more: since migration 037 a
// resident holds no role at all, and a staff member who also lives here holds
// one that would fail a `roles={['resident']}` test. Both are answered by the
// single question this asks — does the server say you may read the portal? —
// which it derives from an open occupancy on every request, so access also
// disappears on the next navigation after a move-out.
export const RequirePermission: React.FC<{
  permission: string;
  children: React.ReactElement;
}> = ({ permission, children }) => {
  const { user, onboarding, permissions, authStatus } = useAuth();

  if (!user) {
    return authStatus === 'loading' ? <Loading /> : <Navigate to="/" replace />;
  }
  if (onboarding && !onboarding.completed) {
    return <Navigate to="/onboarding" replace />;
  }
  if (!permissions.includes(permission)) {
    const home = resolvePostAuthRoute(user, onboarding, permissions);
    /* THE PORTAL, FOR SOMEBODY WHO HAS NO HOME ANY MORE (RP-9.9). A signed-in
       person with no staff role and no open occupancy has nothing else to land
       on, so "home" resolves to the portal itself — and redirecting there
       would loop. They moved out; say that, with a way to sign out, rather
       than a toast about access and (as it used to be) the sign-in form while
       still signed in. */
    if (permission === PERMISSIONS.PORTAL_READ && home === '/portal') {
      return <PortalNoHome />;
    }
    /* IS6: a concierge of an organisation that dropped Desk and has no hotel holds
       no permissions, and their home is /reception, which refused them. Redirecting
       there would loop. The same plain screen, with a way to sign out. */
    if (permission === PERMISSIONS.STAYS_DESK && home === '/reception') {
      return <PortalNoHome />;
    }

    return (
      <RedirectWithReason
        to={home}
        reason="You do not have access to that page. Here is your home screen."
      />
    );
  }

  return children;
};
